import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { ApiResponse } from "../../models/ApiResponse";
import api from "../../services/ApiService";
import { CreateItemRequest } from "../../models/Items/CreateItemRequest";
import { CreateItemDropdownOptions } from "../../models/Items/CreateItemDropdownOptions";
import AttachmentsUploader, { AttachmentsUploaderRef, MultipleAttachment } from "../../components/AttachmentsUploader";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../../components/ConfirmationModal";
import { PrjStageWithStatusAndTag } from "../../models/PrjStage";
import { FIELD_NAMES, PROJECT_MANAGER_ASSIGNMENT, USER_ASSIGNMENTS_VALIDATION_MESSAGES, attachmentLevels, attachmentsValidationMessages, createProductValidations, dataFieldNames, DEVELOPMENT_TYPE_DROPDOWN_OPTIONS } from "./EditItem/Constants";
import Select from '@mui/material/Select';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Tag } from "../../models/Tag";
import Tags from "../../components/Tags";
import UserAssignments, { UserAssignmentFieldValue, UserDropdownOptions } from "../../components/UserAssignments";
import SessionService from "../../services/SessionService";
import { Permissions } from "../../models/Enum";
import { ProductLayoutCategoryDetail } from "../../models/Items/ProductLayoutCategoryDetail";
import { ProductLayoutFieldDetail } from "../../models/Items/ProductLayoutFieldDetail";
import { KeyValues } from "../../models/Items/FilterOptions";
import { User } from "../../models/User";
import { getInputTitle, sortByKey, sortCVLFieldOptions } from "../../utils/helpers";
import { fetchUserAssignmentsFromBaseCode } from "../../components/fetchProductAssignments";
import AddOrSelectAutocomplete from "../../components/AddOrSelectAutocomplete";
import AddSeriesNameModal, { useProductSeriesName } from "../../components/AddSeriesNameModal";

const CreateProduct = () => {

  const [attachments, setAttachments] = useState<MultipleAttachment[]>([]);
  const [dropDownOptions, setDropDownOptions] = useState<CreateItemDropdownOptions>({});
  const [dropdownOptionsLoading, setDropdownOptionsLoading] = useState(true);
  const holdProductSubmitRef = useRef<() => boolean>(() => false);
  const getSeriesNameFormValuesRef = useRef<() => Record<string, any>>(() => ({}));
  const [selectedDropDownOptions, setSelectedDropDownOptions] = useState<any>(
    {}
  );
  const [showResetConfirmationModal, setShowResetConfirmationModal] = useState(false);
  const [fieldsDisabled, setFieldsDisabled] = useState<string[]>([ FIELD_NAMES.status, FIELD_NAMES.function]);
  const [selectedTagOptions, setSelectedTagOptions] = useState<Tag[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [productLayoutFields, setProductLayoutFields] = useState<ProductLayoutFieldDetail[]>([]);
  const [statusDropDownOptions, setStatusDropDownOptions] = useState<KeyValues[]>([]);
  const [tagMenuOptions, setTagMenuOptions] = useState<Tag[]>([]);

  const [selectedUserAssignments, setSelectedUserAssignments] = useState<UserAssignmentFieldValue[] | null>(null);
  const [selectedNewUserAssignments, setSelectedNewUserAssignments] = useState<UserAssignmentFieldValue[]>([]);
  const sessionService = SessionService.getInstance();

  const [prjStageTags, setPrjStageTags] = useState<PrjStageWithStatusAndTag[]>([]);
  const [assignmentRoleDropdownOptions, setAssignmentRoleDropdownOptions] = useState<KeyValues[]>([]);
  const [userDropdownOptions, setUserDropdownOptions] = useState<UserDropdownOptions[]>([]);
  const [formValueBaseCode, setFormValueBaseCode] = useState<string>();
  const [userAssignmentsApiRunning, setUserAssignmentsApiRunning] = useState<boolean>(null);

  let navigate = useNavigate();

  const uploaderRef = useRef<AttachmentsUploaderRef>(null);

  // these fields stores the reference id and not the actual value e.g. Stage, Category
  const REFERENCE_FIELDS = [
    "stage",
    "status",
    "marketingDesigner",
    "category",
    "function",
    "brandCollection",
    "style",
    "skuType",
    "romanticSize",
    "seriesName",
    "developmentType"
  ];

  useEffect(() => {
    getAllDropDownOptions();
    fetchProductLayoutFields();
    fetchStageTag();
    getAssignmentRoleList();
    getUserList();
  }, []);

  const tagIdMap = prjStageTags.map(i => i.tags.map(i => i.id))
  const uniqueTagsIdList = tagIdMap.flat().filter((value, index, self) => self.indexOf(value) === index);
  
  const fetchStageTag = async () => {
      const stageTagResponse = await api.get<ApiResponse<PrjStageWithStatusAndTag[]>>("/Picklist/stages");
      setPrjStageTags(stageTagResponse.data || []);
  }

  /* 
    This function is used to get drop down options data from api
   */
  const getAllDropDownOptions = async () => {
    try {
      // using same filter options that we are using in Product Listing screen + some other drop down using separate API
      const dropDownSources = await api.get<
        ApiResponse<CreateItemDropdownOptions>
      >(`/Item/active-dropdown-options`);

      if (dropDownSources?.isSuccess) {

        if (dropDownSources.data.tags?.length > 0) {
          dropDownSources.data.tags = dropDownSources.data.tags.map(s => (
            {
              id: s.id,
              name: s.name
            }
          ))
          .sort(sortByKey());
        }
        dropDownSources.data.functions = [];
        
        // Sort dropdowns
        sortCVLFieldOptions(dropDownSources.data);
        
        setDropDownOptions(dropDownSources.data);
      }
    } catch (error) {
      console.error("Exception from API", error);
    } finally {
      setDropdownOptionsLoading(false);
    }
  };

  /* 
    This function is used to get assignment role drop down options from api
   */
    const getAssignmentRoleList = () => {
      try {
        api
          .get<ApiResponse<any[]>>(`/Assignment/list`)
          .then((response: ApiResponse<any[]>) => {
            if (response.isSuccess) {
              if (response.data?.length > 0) {
                const assignments = response.data.filter(a => a.isActive === true)
                .map(u => {
                  return {
                    id: u.id,
                    name: u.name
                  }
                });
                setAssignmentRoleDropdownOptions(assignments);
              }
            } else {
              throw new Error(response?.message);
            }
          })
          .catch((error) => {
            console.error("Exception from Assignments", error);
          });
      } catch (error: any) {
        console.error(error.message);
      }
    };

    useEffect(() => {
      if(formValueBaseCode?.trim()) {
        fetchUserAssignmentsFromBaseCode(formValueBaseCode, setSelectedUserAssignments, setUserAssignmentsApiRunning);
        setSelectedNewUserAssignments([{ userId: "", assignmentId: "" }]);
      } else {
        setSelectedUserAssignments(null);
        setSelectedNewUserAssignments([]);
        setUserAssignmentsApiRunning(null);
      }
    }, [formValueBaseCode])

    useEffect(() => {
      if(formik.values[FIELD_NAMES.baseCode]?.trim()) {
        setFormValueBaseCode(formik.values[FIELD_NAMES.baseCode]?.trim());
      }else {
        setFormValueBaseCode("");
      }
    }, [])

  /* 
    This function is used to get user drop down options from api
  */
  const getUserList = () => {
    try {
      api
        .get<ApiResponse<User[]>>(`/UserRole`)
        .then((response: ApiResponse<User[]>) => {
          if (response.isSuccess) {
            if (response.data?.length > 0) {
              const users = response.data.filter((ul) => ul.isActive === true).map(ul => {
                return {
                  id: ul.id,
                  name: ul.name,
                  disabled: false
                }
              });
              setUserDropdownOptions(users);
            }
          } else {
            throw new Error(response?.message);
          }
        })
        .catch((error) => {
          console.error("Exception from User Role", error);
        });
    } catch (error: any) {
      console.error(error.message);
    }
  };

  /**
   * This function is used to fetch edit product layout fields from api and set it to state
   */
  const fetchProductLayoutFields = async () => {
    try {
      const response = await api
        .get<ApiResponse<ProductLayoutCategoryDetail[]>>(`/PickList/layoutFields`);
      if (response.isSuccess) {
        const { data } = response;
        if (data?.length > 0) {
          // Flatten fields array from the api response data
          let flattenedFields = data.flatMap(category => category.fields);

          const prepareLayoutFields = [];
          // Update fields names recieved from api so that it matches with formRenderer keys to avoid casing errors
          flattenedFields.map((field) => {
            const matchedName = formRendererNames.find(
              (fn) => fn.toLowerCase() === field.fieldName.toLowerCase()
            );

            if (matchedName) {
              prepareLayoutFields.push({
                ...field,
                fieldName: matchedName
              });
            }
          });
          setProductLayoutFields(prepareLayoutFields);
        }
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      console.error(error?.message);
    }
  };

  /**
   * This function is used to handle Autocomplete dropdown value
   * @param fieldName - Name of the field
   * @param label - Label of the field
   */
  const handleAutocompleteOptionsChange = (fieldName, selectedItem) => {
    const selectedValue =
      selectedItem ? typeof selectedItem === "string"
        ? selectedItem
        : REFERENCE_FIELDS.includes(fieldName)
          ? selectedItem.id
          : selectedItem.name
        : undefined;
    formik.setFieldValue(fieldName, selectedValue);
    setSelectedDropDownOptions({
      ...selectedDropDownOptions,
      [fieldName]: selectedItem,
    });
    if (fieldName === FIELD_NAMES.category) {
      formik.setFieldValue(fieldName, selectedValue);
      formik.setFieldValue(FIELD_NAMES.function, undefined);
      setSelectedDropDownOptions({
        ...selectedDropDownOptions,
        [fieldName]: selectedItem,
        [FIELD_NAMES.function]: undefined,
      });
      if (selectedItem) {
        handleDropDownFieldsEnabled(FIELD_NAMES.function);
      } else {
        handleDropDownFieldsDisabled(FIELD_NAMES.function);
      }
      prepareFunctionDropDownOptions(selectedItem?.id);

    }
    if (fieldName === FIELD_NAMES.stage) {
      formik.setFieldValue(fieldName, selectedValue);
      formik.setFieldValue(FIELD_NAMES.status, undefined);
      setSelectedDropDownOptions({
        ...selectedDropDownOptions,
        [fieldName]: selectedItem,
        [FIELD_NAMES.status]: undefined,
      });
      if (selectedItem) {
        handleDropDownFieldsEnabled(FIELD_NAMES.status);
      } else {
        handleDropDownFieldsDisabled(FIELD_NAMES.status);
      }
      prepareStatusDropDownOptions(selectedItem?.id);
      prepareTagOptions(selectedItem?.id, selectedTagOptions);
    }
  };

  /**
   * This function is used to handle Select dropdown value
   * @param fieldName - Name of the field
   * @param selectedItem - Selected option value
   */
  const handleDropDownOptionsChange = (fieldName, selectedItem) => {
    const selectedValue =
      typeof selectedItem === "string"
        ? selectedItem
        : REFERENCE_FIELDS.includes(fieldName)
          ? selectedItem.value
          : selectedItem.children;
    formik.setFieldValue(fieldName, selectedValue);

    if (fieldName === FIELD_NAMES.category) {
      formik.setFieldValue(FIELD_NAMES.function, undefined);
      handleDropDownFieldsEnabled(FIELD_NAMES.function);
      prepareFunctionDropDownOptions(selectedItem.value);
      
    }
    if (fieldName === FIELD_NAMES.stage) {
      formik.setFieldValue(FIELD_NAMES.status, undefined);
      handleDropDownFieldsEnabled(FIELD_NAMES.status);
      prepareStatusDropDownOptions(selectedItem.value);
      prepareTagOptions(selectedItem.value, selectedTagOptions);
    }
  };

  /**
     * This function is used to remove array product from state and enable the dropdown field
     * @param fieldName - Name of the field to enable it
     */
  const handleDropDownFieldsEnabled = (fieldName) => {
    const isFieldExists = fieldsDisabled.includes(fieldName);

    if (isFieldExists) {
      // Remove the product from the array
      setFieldsDisabled(fieldsDisabled.filter(sec => sec !== fieldName));
    }
  };

  const handleDropDownFieldsDisabled = (fieldName) => {
    const isFieldNotExists = !fieldsDisabled.includes(fieldName);

    if (isFieldNotExists) {
      setFieldsDisabled([...fieldsDisabled, fieldName]);
    }
  };

  /**
   * This function is used to returns list of DataTypes for a given field name
   * @param categoryName - Category display text field api data
   */
  const prepareFunctionDropDownOptions = (categoryId: number) => {
    if (categoryId && (dropDownOptions.productFunctions.length > 0)) {
      const functionDropDownOptions = dropDownOptions.productFunctions
        .filter(func => func.category === categoryId)
        .map(item => {
          return {
            id: item.id,
            description: item.description,
            leagoCode: item.leagoCode,
            optionLabel: item.optionLabel
              || (item.leagoCode?.trim()
                ? `${item.description?.trim()} (${item.leagoCode.trim()})`
                : item.description),
          }
        });
      setDropDownOptions({
        ...dropDownOptions,
        functions: functionDropDownOptions
      });
    }
  };

  const prepareStatusDropDownOptions = (stageId: number) => {
    if (stageId && dropDownOptions?.stageStatusTags?.length) {
      const statusDropDownOptions = dropDownOptions.stageStatusTags
        .find(stage => stage.id === stageId)
        .statuses
        .map(s => {
          return {
            id: s.id,
            description: s.description,
          }
        });
      setStatusDropDownOptions(statusDropDownOptions);
    }
  };

  const prepareTagOptions = (stageId: number, selectedTags: Tag[]) => {
    if (stageId && dropDownOptions?.stageStatusTags?.length) {
      const stageWiseTagOptionsForDropdown = dropDownOptions.stageStatusTags
        .find(stage => stage.id == stageId)
        .tags
        .filter(t => !selectedTags.some((st) => st.id === t.id))
        .map(s => {
          return {
            id: s.id,
            name: s.name
          }
        });
        
      setTagMenuOptions(stageWiseTagOptionsForDropdown);
    }
  };

  /**
   * This function is used to render autocomplete with dropdown option list
   * @param fieldName - Name of the field
   * @param label - Label of the field
   * @param optionList - Dropdown option array list
   */
  const renderAutocomplete = (
    fieldName: string,
    label: string,
    optionList: Array<any> | undefined
  ) => (
    <FormControl key={fieldName} style={{ margin: "5px 5px", width: "32%" }}>
      <label htmlFor={`outlined-${label}`}>
        {label}
        {([FIELD_NAMES.brandCollection, FIELD_NAMES.developmentType, FIELD_NAMES.stage, FIELD_NAMES.status, FIELD_NAMES.category, FIELD_NAMES.function].includes(fieldName) ) && <span className="required-field-asterisk-icon">{` *`}</span>}
      </label>
      <Autocomplete
        id={`outlined-${label}`}
        value={
          selectedDropDownOptions[fieldName]
            ? selectedDropDownOptions[fieldName]
            : null
        }
        onChange={(event, newValue) =>
          handleAutocompleteOptionsChange(fieldName, newValue)
        }
        onBlur={(e) => {
          if(createProductValidations.fields[fieldName]) {
            formik.validateField(fieldName);
          }
          formik.handleBlur(e);
        }}
        options={dropdownOptionsLoading ? [] : (optionList || [])}
        loading={dropdownOptionsLoading}
        getOptionLabel={(option) => {
          const matchedOption = option?.id != null
            ? (optionList || []).find((item: any) => item?.id === option?.id)
            : undefined;
          const label = matchedOption?.optionLabel || option?.optionLabel || option?.name || option?.description || option || "";
          return label?.toString?.() || "";
        }}
        renderOption={(props, option) => {
          const matchedOption = option?.id != null
            ? (optionList || []).find((item: any) => item?.id === option?.id)
            : undefined;
          const optionText = (matchedOption?.optionLabel || option?.optionLabel || option?.name || option?.description || option || "")?.toString?.() || "";
          return (
            <li {...props} key={option?.id || props.id} title={optionText}>
              {optionText}
            </li>
          );
        }}
        disabled={fieldsDisabled.includes(fieldName)}
        style={{
          backgroundColor: fieldsDisabled.includes(fieldName) ? "#EBEBE4" : "#FFFFFF",
          marginTop: "8px"
        }}
        renderInput={(params) => (
          <TextField
            error={
              Boolean(formik.errors[fieldName])
            }
            label={formik.errors[fieldName] ? formik.errors[fieldName] : ""}
            key={params.id}
            {...params}
            placeholder="- Select -"
            inputProps={{
              ...params.inputProps,
              title: getInputTitle(params.inputProps?.value),
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {dropdownOptionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {formik.touched[fieldName] && formik.errors[fieldName] && (
        <div className={`formik-error-${fieldName}`}></div>
      )}
    </FormControl>
  );

  /**
   * This function is used to render select dropdown option list
   * @param fieldName - Name of the field
   * @param label - Label of the field
   * @param optionList - Dropdown option array list
   */
  const renderDropDown = (
    fieldName: string = "",
    label: string = "",
    optionList: Array<any> | undefined
  ) => (
    <FormControl key={fieldName} style={{ margin: "5px 5px", width: "32%" }}>
      <label
        htmlFor={`outlined-${'fieldName'}`}
        style={{ textTransform: "capitalize" }}
      >
        {label}
        {(fieldName === FIELD_NAMES.stage || fieldName === FIELD_NAMES.status || fieldName === FIELD_NAMES.category || fieldName === FIELD_NAMES.function) && 
        <span className="required-field-asterisk-icon">{` *`}</span>}
      </label>
      <Select
        id={`outlined-${fieldName}`}
        name={fieldName}
        type="text"
        onBlur={(e) => {
          if(createProductValidations.fields[fieldName]) {
            formik.validateField(fieldName);
          }
          formik.handleBlur(e);
        }}
        value={formik.values[fieldName] || ""}
        displayEmpty={true}
        onChange={(event, selectedItem: any) =>
          handleDropDownOptionsChange(fieldName, selectedItem.props)
        }
        renderValue={(selected) => {
          if (!selected) {
            return formik.errors[fieldName] ? formik.errors[fieldName] : `Select`;
          }
          const selectedOption = optionList?.find((item) => item.id === selected);
          return selectedOption?.name || selectedOption?.description || selected;
        }}
        SelectDisplayProps={{
          title: (() => {
            const selectedOption = optionList?.find((item) => item.id === formik.values[fieldName]);
            return getInputTitle(selectedOption?.name || selectedOption?.description);
          })(),
        }}
        disabled={fieldsDisabled.includes(fieldName)}
        sx={{
          backgroundColor: fieldsDisabled.includes(fieldName) ? "#EBEBE4" : "#FFFFFF",
          marginTop: "8px"
        }}
        style={{color: Boolean(formik.errors[fieldName]) && "#d32f2f"}}
        error={
          Boolean(formik.errors[fieldName])
        }
      >
        <MenuItem value="" disabled>Select</MenuItem>
        {optionList && optionList.length > 0 &&
          optionList.map((item) => {
            const optionText = item.description || item.name;
            return (
              <MenuItem key={item.id} value={item.id} title={optionText}>
                {optionText}
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );

  /**
   * This function is used to render text field
   * @param fieldName - Name of the field
   * @param label - Label of the field
   * @param numeric - Boolean for numeric type field
   */
  const renderTextField = (fieldName, label, numeric = false) => (
    <FormControl key={fieldName} style={{ margin: "5px 5px", width: "32%" }}>
      <label htmlFor={`outlined-${label}`}>
        {label}
        {(fieldName === FIELD_NAMES.shortDesc) && <span className="required-field-asterisk-icon">{` *`}</span>}
      </label>
      <TextField
        id={`outlined-${label}`}
        value={formik.values[fieldName] ?? ""}
        label={formik.errors[fieldName] ? formik.errors[fieldName] : ""}
        onChange={formik.handleChange}
        onBlur={(e) => {
          if(createProductValidations.fields[fieldName]) {
            formik.validateField(fieldName);
          }
          formik.handleBlur(e);
        }}
        name={fieldName}
        onKeyDown={(e) => (e.key === "ArrowUp" || e.key === "ArrowDown") && e.preventDefault()}
        onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
        className="hide-input-arrow"
        inputProps={{ type: numeric ? 'number' : 'text' }}
        error={Boolean(formik.errors[fieldName])}
        style={{ marginTop: "8px" }}
      />
      {formik.errors[fieldName] && (
        <div className={`formik-error-${fieldName}`}></div>
      )}
    </FormControl>
  );

    /**
   * This function is used to render text field for Base Code Automation
   * @param fieldName - Name of the field
   * @param label - Label of the field
   */
    const renderTextFieldBaseCodeAuto = (fieldName, label, numeric = false) => (
      <FormControl key={fieldName} style={{ margin: "5px 5px", width: "32%" }}>
        <label htmlFor={`outlined-${label}`}>
          {label}
          {(fieldName === FIELD_NAMES.shortDesc) && <span className="required-field-asterisk-icon">{` *`}</span>}
        </label>
        <TextField
          id={`outlined-${label}`}
          value={formik.values[fieldName] ?? ""}
          label={formik.errors[fieldName] ? formik.errors[fieldName] : ""}
          onChange={formik.handleChange}
          onBlur={(e) => {
            if(createProductValidations.fields[fieldName]) {
              formik.validateField(fieldName);
            }
            formik.handleBlur(e);
            if(fieldName === FIELD_NAMES.baseCode){
              setFormValueBaseCode(e.target.value);
            }
          }}
          name={fieldName}
          onKeyDown={(e) => (e.key === "ArrowUp" || e.key === "ArrowDown") && e.preventDefault()}
          onFocus={(e) => e.target.addEventListener("wheel", function (e) { e.preventDefault() }, { passive: false })}
          className="hide-input-arrow"
          InputProps={{ 
            endAdornment:
            <InputAdornment position="end">
              {userAssignmentsApiRunning 
                ? <Box className="baseCode-loading-box">
                  <CircularProgress color="primary" className="baseCode-loading" />
                </Box> 
                : <AutoAwesomeOutlinedIcon onClick = {handleBaseCodeAuto} sx = {{cursor:"pointer"}}/>}
            </InputAdornment>
          }}
          error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
          style={{ marginTop: "8px" }}
        />
        {formik.touched[fieldName] && formik.errors[fieldName] && (
          <div className={`formik-error-${fieldName}`}></div>
        )}
      </FormControl>
    );

  /**
   * This function is used to Remove attachment from the attachments state
   * @param index - Array index of attachment list
   */
  const handleRemoveAttachment = (index: number) => {
    const updatedAttachments = attachments.filter(
      (a, i) => i !== index
    );
    setAttachments(updatedAttachments);
  };

  // This function is used to reset the Form and Attachment(s)
  const handleFormReset = () => {
    setSelectedDropDownOptions({});
    setAttachments([]);
    setSelectedUserAssignments(null);
    formik.resetForm();
    setShowResetConfirmationModal(false);
    setFormValueBaseCode("");
  };

  const handleCloseModal = () => {
    setShowResetConfirmationModal(false);
  };

  const initialValues: CreateItemRequest = {
    stockcode: null,
    brandCollection: null,
    shortDesc: "",
    // familyName: "",
    seriesName: null,
    seriesNameDisplayText: "",
    pdFamilyName: "",
    baseCode: null,
    pdCode: "",
    stage: null,
    status: null,
    marketingDesigner: null,
    category: null,
    function: null,
    style: null,
    skuType: null,
    drawingNum: "",
    romanticSize: null,
    targetWholesalePrice: null,
    targetIMAP: null,
    tags: [],
    userAssignments: [],
    bC_DesignerSeq: null,
    bC_CategorySeq: null,
  };

  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: createProductValidations,
    onSubmit: async () => {
      if (holdProductSubmitRef.current()) {
        return;
      }

      let isValid = true;
      const formData = new FormData();
      
      // error messages to display in toast
      const newErrors: any = {};

      const containsLevelBC = attachments.some((a) => a.lvl === attachmentLevels.BC);
      const containsLevelSC = attachments.some((a) => a.lvl === attachmentLevels.SC);

      // Add attachment validation check
      if (attachments && attachments.length > 0) {
        const {
          stockCodeToastError,
          baseCodeToastError,
          stockCodeInputBoxError,
          baseCodeInputBoxError,
          attachmentTypeToastError
        } = attachmentsValidationMessages;

        if ((containsLevelBC && !(formik.values[FIELD_NAMES.baseCode])) ||
          (containsLevelBC && formik.values[FIELD_NAMES.baseCode].trim() === "")) {
          formik.setErrors({ baseCode: baseCodeInputBoxError });
          newErrors.baseCode = baseCodeToastError;
          isValid = false;
        }

        if ((containsLevelSC && !(formik.values[FIELD_NAMES.stockcode])) ||
          (containsLevelSC && formik.values[FIELD_NAMES.stockcode].trim() === "")) {
          formik.setErrors({ stockcode: stockCodeInputBoxError });
          newErrors.stockCode = stockCodeToastError;
          isValid = false;
        }

        const checkAttachmentType = attachments.some((a) => a.attachType == 0 && a.disabled === false);

        if (checkAttachmentType) {
          newErrors.attachType = attachmentTypeToastError;
          isValid = false;
        }

        if (isValid === true) {
          // Append form data if Attachment type contains BC / SC Level
          if (containsLevelBC) {
            formData.append(FIELD_NAMES.baseCode, (formik.values[FIELD_NAMES.baseCode]).trim());
          }
          if (containsLevelSC) {
            formData.append(FIELD_NAMES.stockcode, (formik.values[FIELD_NAMES.stockcode]).trim());
          }
        }
      }

      // Add user assignment validation check
      if(selectedNewUserAssignments?.length > 0) {
        const checkFirstRowUserAssignment = selectedNewUserAssignments.some((ua) => ua.userId && ua.assignmentId === "");
        
        const checkUserFromSecondRow = selectedNewUserAssignments.slice(1).some((ua) => ua.userId === "");
        const checkAssignmentFromSecondRow = selectedNewUserAssignments.slice(1).some((ua) => ua.assignmentId === "");

        const { userError, assignmentRoleError } = USER_ASSIGNMENTS_VALIDATION_MESSAGES;
        if(checkFirstRowUserAssignment) {
          newErrors.assignmentId = assignmentRoleError;
          isValid = false;
        }

        if(checkUserFromSecondRow) {
          newErrors.userId = userError;
          isValid = false;
        }

        if(checkAssignmentFromSecondRow) {
          newErrors.assignmentId = assignmentRoleError;
          isValid = false;
        }
      }
      
      // Check if there are any errors
      if (isValid === false) {
        // Combine all error messages
        const errorMessage = Object.values(newErrors).join(', ');
        // Display the combined error message in a toast notification
        toast.error(errorMessage);
        setDisableSubmit(false);
      }

      try {
        if (isValid === true) {
            submitFormDataToItemApi();
        }
      } catch (error: any) {
        setDisableSubmit(false);
        console.error("Exception from create product", error);
      }
    }
  });

  /**
   * This function is used to submit product data to api
   */
  const submitFormDataToItemApi = async (newUserAssignments = null) => {
    const containsLevelBC = attachments.some((a) => a.lvl === attachmentLevels.BC);
    const containsLevelSC = attachments.some((a) => a.lvl === attachmentLevels.SC);

    const data = {
      ...formik.values,
      ...getSeriesNameFormValuesRef.current(),
    };

    // Add tags data to submit in api request
    data.tags = (selectedTagOptions || []).map(t => t.id);

    // checking if userassignments had atleast one correct entry
    if(selectedNewUserAssignments?.[0]?.userId && selectedNewUserAssignments?.[0]?.assignmentId) {
      data.userAssignments = selectedNewUserAssignments
    } else {
      data.userAssignments = null;
    }

    // Trim stockcode if it contains value
    if (data.stockcode) {
      data.stockcode = data.stockcode.trim();
    }
    // Trim basecode if it contains value
    if (data.baseCode) {
      data.baseCode = data.baseCode.trim();
    }

    setDisableSubmit(true);

    try {
      const createItemResponse = await api.post<ApiResponse<CreateItemRequest>>(
        "/Item",
        data
      );

      // Append Product id in Form data to submit in api request
      const itemId = createItemResponse?.data;

      if (createItemResponse?.isSuccess) {
        toast.success(createItemResponse?.message);

        if (uploaderRef.current) {
          // Handle the uploading of attachments to API, including small and large files,
          // by separating them into appropriate categories and uploading them accordingly.
          await uploaderRef.current.uploadAttachmentsToAPI(itemId, formik, containsLevelBC, containsLevelSC);
        }
      }
      navigate("/products");
    } catch (err) {
      setDisableSubmit(false);
    }
  };

  const {
    seriesNameOptions,
    addSeriesNameModalProps,
    openAddSeriesName,
    rememberSeriesNameInput,
    holdProductSubmitForNewSeriesName,
    getSeriesNameFormValues,
    lockedSeriesRef,
  } = useProductSeriesName({
    formik,
    selectedSeriesName: selectedDropDownOptions?.seriesName,
    setSelectedDropDownOptions,
    dropDownSeriesNames: dropDownOptions.seriesNames,
  });
  holdProductSubmitRef.current = holdProductSubmitForNewSeriesName;
  getSeriesNameFormValuesRef.current = getSeriesNameFormValues;

  const getFieldLabel = (fieldName) => {
    if (productLayoutFields?.length > 0) {
      let fieldLabel;
      productLayoutFields.find((fl) => {
        if (fl.fieldName === fieldName) {
          fieldLabel = fl.label;
        }
      });
      return fieldLabel || fieldName;
    }
  };

  //TODO: can be use as component
  //This validate the required filed for basecode automation
  const validateBaseCodeAuto = (missingFields) => {
    const { marketingDesigner, seriesName, category, seriesNameDisplayText } = formik.values;
    
    if (!marketingDesigner) {
      missingFields.push(getFieldLabel(FIELD_NAMES.marketingDesigner));
    };
    if (!seriesName && !seriesNameDisplayText) {
      missingFields.push(getFieldLabel(FIELD_NAMES.seriesName));
    };
    if (!category) {
      missingFields.push(getFieldLabel(FIELD_NAMES.category));
    };
  }

  //TODO: can be use as component
  //This call the basecode API and set the basecode field ONLY to the response  
  const handleBaseCodeAuto = async () => {
    const missingFields = [];
    validateBaseCodeAuto(missingFields);
    if (missingFields.length > 0) {
      toast.error(`${missingFields.join(', ')} ${missingFields.length > 1 ? 'are' : 'is'} required to generate Base Code`);
    } else {
      try {
        const baseCodeAutoResponse = await api.post<ApiResponse<any>>("/Item/base-code-automation", {
          marketingDesigner: formik.values.marketingDesigner,
          seriesName: formik.values.seriesName,
          seriesNameDisplayText: formik.values.seriesNameDisplayText,
          category: formik.values.category,
        })
        if (baseCodeAutoResponse.isSuccess) {
          formik.setFieldValue(FIELD_NAMES.baseCode, baseCodeAutoResponse?.data?.baseCode);
          if (!(formik.values.pdCode)) {
            formik.setFieldValue(FIELD_NAMES.pdCode, baseCodeAutoResponse?.data?.baseCode);
          }
          formik.setFieldValue(FIELD_NAMES.bC_DesignerSeq, baseCodeAutoResponse?.data?.designerSeqNo);
          formik.setFieldValue(FIELD_NAMES.bC_CategorySeq, baseCodeAutoResponse?.data?.categorySeqNo);
          formik.setFieldValue(FIELD_NAMES.seriesName, baseCodeAutoResponse?.data?.seriesName);
          setSelectedDropDownOptions({
            ...selectedDropDownOptions,
            seriesName: {id: baseCodeAutoResponse?.data?.seriesName, name: formik?.values?.seriesNameDisplayText}
          });
          setFormValueBaseCode(baseCodeAutoResponse?.data?.baseCode);
          toast.success(baseCodeAutoResponse.message);
        }
      } catch (error: any) {
        toast.error(error.message);
      }
    }
  }

  /**
   * This function is used to handle tag option selection
   * @param tagToAdd - Selected Tag Option
   */
  const handleTagOptionSelect = (tagToAdd: Tag) => {
    // Add tag in selected tag option to display selected tags button
    setSelectedTagOptions([...selectedTagOptions, tagToAdd]);
    // Remove the selected tag from tag menus so that it cannot be selected again
    let updateTagOptions = tagMenuOptions.filter(t => t.id !== tagToAdd.id);
    setTagMenuOptions(updateTagOptions);
  };

  /**
   * This function is used to handle remove selected tag
   * @param tagId - Tag Id
   */
  const handleRemoveTag = (tagId: number) => {
    // Remove tag from selected tag state
    const tagsAfterRemoval = selectedTagOptions.filter((st) => st.id !== tagId);
    // Add tag in tag menu options which is currently removed from selected tag
    const removedTagInfo = selectedTagOptions.find(t => t.id === tagId);
    const sortedTagMenuOptions = [...tagMenuOptions, removedTagInfo].sort((a, b) => a.id > b.id ? 1 : -1);
    setSelectedTagOptions(tagsAfterRemoval);
    setTagMenuOptions(sortedTagMenuOptions); 
  };
  
  useEffect(() => {
    const errorFields = Object.keys(formik.errors);

    if (errorFields.length > 0) {
      formik.setTouched({ [errorFields[0]]: true }, false);

      const firstErrorField = document.querySelector(
        `.formik-error-${errorFields[0]}`
      );

      if (firstErrorField) {
        firstErrorField.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      }
    }
  }, [formik.errors]);

  /**
   * This function is used to returns list of DataTypes for a given field name
   * @param attributeName - Api response field name
   */
  const getAttributeValues = (attributeName: string) => {
    if (dropDownOptions && dropDownOptions.dataFieldsTypes) {
      return dropDownOptions.dataFieldsTypes
        ?.find(df => df.field.toLowerCase() === attributeName.toLowerCase())
        ?.types
        ?.map(t => {
          return {
            id: t.id,
            description: t.description,
            optionLabel: t.optionLabel,
          }
        });
    }
    else return [];
  };

  const formFieldsRenderer = {
    brandCollection: (field) => () => renderAutocomplete(field.fieldName, field.label, getAttributeValues(dataFieldNames.brandCollection)),
    marketingDesigner: (field) => () => renderAutocomplete(field.fieldName, field.label, dropDownOptions.productDesignerMkts),
    seriesName: (field) => () => (
      <AddOrSelectAutocomplete
        fieldName={field.fieldName}
        label={field.label}
        options={seriesNameOptions}
        formik={formik}
        setSelectedDropDownOptions={setSelectedDropDownOptions}
        selectedDropDownOptions={selectedDropDownOptions}
        style={{
          backgroundColor: fieldsDisabled.includes(field.fieldName) ? "#EBEBE4" : "#FFFFFF",
          marginTop: "8px"
        }}
        disabled={fieldsDisabled.includes(field.fieldName)}
        onNewValueBlur={openAddSeriesName}
        onInputChange={rememberSeriesNameInput}
        loading={dropdownOptionsLoading}
        suspendCommit={addSeriesNameModalProps.isOpen}
        lockedSeriesRef={lockedSeriesRef}
      />
    ),
    pdFamilyName: (field) => () => renderTextField(field.fieldName, field.label),
    shortDesc: (field) => () => renderTextField(field.fieldName, field.label),
    romanticSize: (field) => () => renderAutocomplete(field.fieldName, field.label, getAttributeValues(dataFieldNames.romanticSize)),
    category: (field) => () => renderAutocomplete(field.fieldName, field.label, dropDownOptions.productcategories),
    function: (field) => () => renderAutocomplete(field.fieldName, field.label, dropDownOptions.functions),
    baseCode: (field) => () => renderTextFieldBaseCodeAuto(field.fieldName, field.label),
    stockcode: (field) => () => renderTextField(field.fieldName, field.label),
    stage: (field) => () => renderAutocomplete(field.fieldName, field.label, dropDownOptions.stageStatusTags),
    status: (field) => () => renderAutocomplete(field.fieldName, field.label, statusDropDownOptions),
    style: (field) => () => renderAutocomplete(field.fieldName, field.label, dropDownOptions.styles),
    skuType: (field) => () => renderAutocomplete(field.fieldName, field.label, dropDownOptions.skuTypes),
    targetWholesalePrice: (field) => () => renderTextField(field.fieldName, field.label, true),
    targetIMAP: (field) => () => renderTextField(field.fieldName, field.label, true),
    drawingNum: (field) => () => renderTextField(field.fieldName, field.label),
    pdCode: (field) => () => renderTextField(field.fieldName, field.label),
    developmentType: (field) => () => renderAutocomplete(field.fieldName, field.label, DEVELOPMENT_TYPE_DROPDOWN_OPTIONS),
  };

  const formRendererNames = Object.keys(formFieldsRenderer);

  return (
    <>
      <ConfirmationModal
        isOpen={showResetConfirmationModal}
        onClose={handleCloseModal}
        title={"Confirm Reset Form"}
        message={"Are you sure you want to reset this form?"}
        handleConfirmYes={handleFormReset}
        handleConfirmNo={handleCloseModal}
      />
      <AddSeriesNameModal {...addSeriesNameModalProps} />
      <Card sx={{ marginBottom: 1 }}>
        <CardContent className="create-new-product">
          <p>Create New Product</p>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            <form onSubmit={formik.handleSubmit}>
              {sessionService.hasPermission(Permissions.ManageTags) && Object.keys(dropDownOptions).length > 0 && (
                <Tags
                  selectedTagOptions={selectedTagOptions}
                  tagDropDownOptions={tagMenuOptions}
                  handleRemoveTag={handleRemoveTag}
                  handleTagOptionSelect={handleTagOptionSelect}
                  uniqueTagsIdList = {uniqueTagsIdList}
                />
              )}

              {productLayoutFields?.length > 0 && productLayoutFields.map(field => {
                const renderFunction = formFieldsRenderer[formRendererNames.find((fieldName) => fieldName.toLowerCase() === field.fieldName.toLowerCase())]; // Use the camelCase version of the field name
                return renderFunction ? renderFunction(field)() : null;
              })}

              {!!formValueBaseCode?.trim() && userAssignmentsApiRunning === false && sessionService.hasPermission(Permissions.ManageProductAssignments) &&
                <UserAssignments
                  selectedUserAssignments={selectedUserAssignments?.length > 0 ? selectedUserAssignments : selectedNewUserAssignments}
                  setSelectedUserAssignments={selectedUserAssignments?.length > 0 ? setSelectedUserAssignments : setSelectedNewUserAssignments}
                  viewMode="CreateProduct"
                  assignmentRoleDropdownOptions={(Array.isArray(assignmentRoleDropdownOptions) ? assignmentRoleDropdownOptions.sort(sortByKey()) : []) as KeyValues[]}
                  userDropdownOptions={(Array.isArray(userDropdownOptions) ? userDropdownOptions.sort(sortByKey()) : []) as UserDropdownOptions[]}
                  classNames="product-page"
                  baseCode={formValueBaseCode}
                  editable={selectedUserAssignments?.length === 0}
                />}

              {(sessionService.hasPermission(Permissions.ManageAttachments)) &&
                <AttachmentsUploader
                  title="Upload"
                  onRemove={handleRemoveAttachment}
                  attachments={attachments}
                  setAttachments={setAttachments}
                  attachmentTypeDropdownOptions={dropDownOptions.attachmentTypes}
                  setDisableSubmit={setDisableSubmit}
                  ref={uploaderRef}
                />}
              <Button
                type="submit"
                variant="contained"
                style={{ margin: "8px 5px", width: "135px" }}
                disabled={disableSubmit}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                style={{ margin: "8px 5px", width: "135px" }}
                onClick={() => setShowResetConfirmationModal(true)}
              >
                Reset
              </Button>
            </form>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default CreateProduct;
